import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { primaryButtonSx, pageTabsSx } from '../../styles/adminPageStyles';
import PageHeader from '../../components/PageHeader';
import { Box, Paper, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import CheckTemplateManagement from './CheckTemplateManagement';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { fetchBusinessFeaturePermissions } from '../../utils/businessPermissions';
import { resolveTemplateTabLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

const ALL_TEMPLATE_TABS = ['safety', 'loan', 'delivery', 'location'] as const;
type TemplateTabId = (typeof ALL_TEMPLATE_TABS)[number];

const Templates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [allowLocationChecks, setAllowLocationChecks] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { adminUiLabels } = useAdminUiLabels();

  useEffect(() => {
    fetchBusinessFeaturePermissions()
      .then((perms) => {
        setAllowLocationChecks(perms.allowLocationChecks);
      })
      .catch(() => {})
      .finally(() => setPermissionsLoaded(true));
  }, []);

  const visibleTabs = useMemo((): TemplateTabId[] => {
    const tabs: TemplateTabId[] = ['safety', 'loan', 'delivery'];
    if (allowLocationChecks) tabs.push('location');
    return tabs;
  }, [allowLocationChecks]);

  const tabParam = searchParams.get('tab') as TemplateTabId | null;
  const activeTabId: TemplateTabId = useMemo(() => {
    if (tabParam && visibleTabs.includes(tabParam)) return tabParam;
    return 'safety';
  }, [tabParam, visibleTabs]);

  const tabIndex = Math.max(0, visibleTabs.indexOf(activeTabId));

  useEffect(() => {
    if (!permissionsLoaded) return;
    if (tabParam && !visibleTabs.includes(tabParam)) {
      if (activeTabId === 'safety') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab: activeTabId }, { replace: true });
      }
    }
  }, [permissionsLoaded, tabParam, visibleTabs, activeTabId, setSearchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    const tabId = visibleTabs[newIndex] ?? 'safety';
    if (tabId === 'safety') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: tabId }, { replace: true });
    }
  };

  const handleSyncTemplateQuestions = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/checks/sync-template-questions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Successfully synced template questions!\n\nStats:\nTotal Checks: ${response.data.stats.totalChecks}\nUpdated: ${response.data.stats.updatedChecks}\nSkipped: ${response.data.stats.skippedChecks}`);
      } else {
        alert('Failed to sync template questions: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error syncing template questions:', error);
      alert('Error syncing template questions. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  if (!permissionsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
      <PageHeader
        title="Templates"
        subtitle="Operations"
        action={
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncTemplateQuestions}
            disabled={syncing}
            sx={primaryButtonSx}
          >
            {syncing ? 'Syncing…' : 'Sync Template Questions'}
          </Button>
        }
      />
      <Paper sx={{ borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ ...pageTabsSx, mb: 0 }}
        >
          <Tab label={resolveTemplateTabLabel('nav_safety_checks', adminUiLabels)} />
          <Tab label={resolveTemplateTabLabel('nav_loan_checks', adminUiLabels)} />
          <Tab label={resolveTemplateTabLabel('nav_delivery_checks', adminUiLabels)} />
          {allowLocationChecks && (
            <Tab label={resolveTemplateTabLabel('nav_location_checks', adminUiLabels)} />
          )}
        </Tabs>
        <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
          {activeTabId === 'safety' && <CheckTemplateManagement filterTag="safety" />}
          {activeTabId === 'loan' && <CheckTemplateManagement filterTag="loan" />}
          {activeTabId === 'delivery' && <CheckTemplateManagement filterTag="delivery" />}
          {activeTabId === 'location' && allowLocationChecks && (
            <CheckTemplateManagement filterTag="location" />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Templates;
